import { useState } from 'react';
import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { Loading, useGetList } from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { marketsForScope } from '../dataProvider/types';
import type { RateRule } from '../dataProvider';
import {
  LIFECYCLE_COLOR,
  LIFECYCLE_LABEL,
  formatEffect,
  formatScope,
  formatWindow,
  ruleLifecycle,
  type RuleLifecycle,
} from './rateRuleState';

/**
 * Rate rule calendar (D-rate-rules) — a DISPLAY over the DERIVED lifecycle states
 * (off the injected clock), grouping the scheduled / live / expired / draft /
 * paused rules with their windows. It computes nothing the resolver does not; it
 * is the scheduling lens over what the rules already say. OPERATIONAL market-scoped
 * and role-gated to editor and super_admin.
 */

const ORDER: RuleLifecycle[] = [
  'live',
  'scheduled',
  'draft',
  'paused',
  'expired',
];

const RuleCard = ({ rule }: { rule: RateRule }) => (
  <Card
    variant="outlined"
    sx={{ mb: 1 }}
    data-testid={`calendar-rule-${rule.id}`}
  >
    <CardContent sx={{ py: 1.5 }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
      >
        <Typography variant="body1" sx={{ fontWeight: 600, minWidth: 180 }}>
          {rule.displayLabel ?? '(unlabelled)'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatScope(rule)} | {formatEffect(rule)} |{' '}
          {String(rule.market).toUpperCase()}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatWindow(rule)}
        </Typography>
      </Stack>
    </CardContent>
  </Card>
);

/** The calendar without the access wrapper. Exported so tests can inject `asOf`. */
export const RuleCalendarView = ({ asOf }: { asOf?: number }) => {
  const [now] = useState<number>(() => asOf ?? Date.now());
  const { activeRegion } = useRegion();
  const markets = [...marketsForScope(activeRegion ?? 'GLOBAL'), 'all'];
  const { data: rules = [], isLoading } = useGetList<RateRule>('rateRules', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'priority', order: 'DESC' },
    filter: { market: markets },
  });

  const byState = new Map<RuleLifecycle, RateRule[]>();
  for (const rule of rules) {
    const state = ruleLifecycle(rule, now);
    const list = byState.get(state) ?? [];
    list.push(rule);
    byState.set(state, list);
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000, mx: 'auto' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1 }}
      >
        Rate rules
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Rule calendar
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The scheduling lens over the rules in scope. The currently-live state is
        derived from each rule&apos;s status and its window. The live per-click
        resolution runs server-side; this is a planning view over today&apos;s
        rules.
      </Typography>

      {isLoading ? (
        <Loading />
      ) : (
        ORDER.map((state) => {
          const list = byState.get(state) ?? [];
          return (
            <Box
              key={state}
              sx={{ mb: 3 }}
              data-testid={`calendar-section-${state}`}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Chip
                  size="small"
                  color={LIFECYCLE_COLOR[state]}
                  label={LIFECYCLE_LABEL[state]}
                  variant={state === 'live' ? 'filled' : 'outlined'}
                />
                <Typography variant="subtitle2">({list.length})</Typography>
              </Stack>
              {list.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None.
                </Typography>
              ) : (
                list.map((rule) => <RuleCard key={rule.id} rule={rule} />)
              )}
            </Box>
          );
        })
      )}
    </Box>
  );
};

export const RuleCalendar = () => (
  <RequireAccess roles={['editor', 'super_admin']}>
    <RuleCalendarView />
  </RequireAccess>
);
